import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Check, Clock, AlertCircle, Pen, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import SignaturePad from '@/components/tools/SignaturePad';
import { DealDocument, DocumentStatus, useDealPipeline } from '@/hooks/useDealPipeline';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface DocumentSignatureCardProps {
  document: DealDocument;
  onSigned?: () => void;
}

const statusConfig: Record<DocumentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'outline', icon: <FileText className="h-3 w-3" /> },
  pending_signature: { label: 'Pending', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  partially_signed: { label: 'Partially Signed', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  signed: { label: 'Signed', variant: 'default', icon: <Check className="h-3 w-3" /> },
  expired: { label: 'Expired', variant: 'destructive', icon: <AlertCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <AlertCircle className="h-3 w-3" /> },
};

export function DocumentSignatureCard({ document, onSigned }: DocumentSignatureCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { signDocument, loading } = useDealPipeline();
  const [signature, setSignature] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const status = statusConfig[document.status];
  const hasUserSigned = document.signatures.some(s => s.user_id === user?.id);
  const needsUserSignature = document.required_signers.includes(user?.id || '') && !hasUserSigned;
  const signedCount = document.signatures.length;
  const totalRequired = document.required_signers.length;

  // Les NDA ne se signent plus dans cette carte — le flux canonique passe
  // par /tools/nda-generator + saveSignedNDA (Cloud Function), seul chemin
  // qui met à jour signedNDAs ET dealParticipants (accès réel au Deal
  // Room). Signer ici échouerait silencieusement côté accès même si le
  // document Firestore affichait "signed". Voir useDealPipeline.signDocument().
  const isNda = document.document_type === 'nda';

  const handleSign = async () => {
    if (!signature) return;

    const role = document.seller_id === user?.id ? 'seller' : 'buyer';
    const success = await signDocument(document.id, signature, role);
    
    if (success) {
      setIsDialogOpen(false);
      setSignature('');
      onSigned?.();
    }
  };

  const handleGoToNdaGenerator = () => {
    const isLender = (user as any)?.roles?.includes('lender');
    const params = new URLSearchParams({
      deal: document.deal_id,
      recipientType: isLender ? 'lender' : 'buyer',
    });
    navigate(`/tools/nda-generator?${params.toString()}`);
  };

  return (
    <Card className={cn(
      'transition-all',
      needsUserSignature && 'border-primary shadow-md'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {document.document_type.toUpperCase()}
          </CardTitle>
          <Badge variant={status.variant} className="flex items-center gap-1">
            {status.icon}
            {status.label}
          </Badge>
        </div>
        <CardDescription>
          Created {new Date(document.created_at).toLocaleDateString()}
          {document.expires_at && (
            <> · Expires {new Date(document.expires_at).toLocaleDateString()}</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Signature progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Signatures</span>
            <span className="font-medium">{signedCount} / {totalRequired}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(signedCount / totalRequired) * 100}%` }}
            />
          </div>
        </div>

        {/* Signers list */}
        <div className="space-y-2">
          {document.required_signers.map((signerId) => {
            const sig = document.signatures.find(s => s.user_id === signerId);
            const isCurrentUser = signerId === user?.id;
            
            return (
              <div 
                key={signerId} 
                className={cn(
                  'flex items-center justify-between p-2 rounded-lg',
                  isCurrentUser && 'bg-muted/50'
                )}
              >
                <span className="text-sm">
                  {isCurrentUser ? 'You' : sig?.role || 'Other party'}
                  {isCurrentUser && !sig && ' (pending)'}
                </span>
                {sig ? (
                  <Badge variant="outline" className="text-green-600">
                    <Check className="h-3 w-3 mr-1" />
                    Signed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                )}
              </div>
            );
          })}
        </div>

        {/* Sign action */}
        {needsUserSignature && document.status !== 'expired' && document.status !== 'rejected' && (
          isNda ? (
            <Button className="w-full" onClick={handleGoToNdaGenerator}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Sign via NDA Generator
            </Button>
          ) : (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Pen className="h-4 w-4 mr-2" />
                  Sign Document
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Sign {document.document_type.toUpperCase()}</DialogTitle>
                  <DialogDescription>
                    Please review the document and provide your electronic signature below.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <SignaturePad
                    signature={signature}
                    onSignatureChange={setSignature}
                  />
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSign}
                      disabled={!signature || loading}
                      className="flex-1"
                    >
                      {loading ? 'Signing...' : 'Confirm Signature'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )
        )}

        {hasUserSigned && (
          <div className="text-center text-sm text-green-600 flex items-center justify-center gap-1">
            <Check className="h-4 w-4" />
            You have signed this document
          </div>
        )}
      </CardContent>
    </Card>
  );
}